import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { MdAdd, MdPlusOne } from "react-icons/md";
import { Link, useHistory, useParams } from "react-router-dom";
import { AccountAvatar } from "../../components/AccountAvatar";
import { Loader } from "../../components/Loader";
import { Title } from "../../components/Title";
import { ICreateMedicalFormValues, MedicalCardForm } from "../../forms";
import {
  GetMedicalCardsDocument,
  useCreateMedicalCardMutation,
  useGetMedicalCardsQuery,
} from "../../graphql";
import { formatDateTime } from "../../utils";

export const MedicalCards: React.FC<{ clientId: number; silent?: boolean }> = ({
  clientId,
  silent = false,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();

  const [createMedicalCard] = useCreateMedicalCardMutation();
  const { data, loading } = useGetMedicalCardsQuery({
    variables: {
      patientId: clientId,
    },
  });

  const onMedicalCardCreate = async (values: ICreateMedicalFormValues) => {
    const { data, errors } = await createMedicalCard({
      variables: {
        diagnosis: values.diagnosis,
        patientId: clientId,
        recipe: values.recipe,
        recommendation: values.recommendation,
        symptoms: values.symptoms,
        privateNote: values.privateNote,
        medicalConcernCategoryId: parseInt(values.medicalConcernCategoryId),
      },
      refetchQueries: [
        {
          query: GetMedicalCardsDocument,
          variables: {
            patientId: clientId,
          },
        },
      ],
    });

    onClose();

    toast({
      title: t("patients.medicalCards.success"),
      description: t("patients.medicalCards.createdSuccessfuly"),
      status: "success",
      isClosable: true,
    });

    if (!silent) {
      history.push(
        `/account/${data.createMedicalCard.patient.id}/visit/${data.createMedicalCard.id}`
      );
    }
  };
  return (
    <>
      <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("patients.medicalCards.add")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <MedicalCardForm onSubmit={onMedicalCardCreate} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Box
        borderWidth="1px"
        borderRadius="lg"
        overflow="hidden"
        p={2}
        background="white"
      >
        <>
          <Title title={t("patients.medicalCards.title")}>
            <Button
              rightIcon={<MdAdd />}
              colorScheme="blue"
              variant="outline"
              onClick={onOpen}
              size="sm"
            >
              {t("patients.medicalCards.add")}
            </Button>
          </Title>
          {loading ? (
            <Loader />
          ) : (
            <Table variant="striped" size="sm">
              <Thead>
                <Tr>
                  <Th>{t("patients.medicalCards.doctor")}</Th>
                  <Th>{t("patients.medicalCards.documents")}</Th>
                  <Th>{t("patients.medicalCards.comments")}</Th>
                  <Th isNumeric>{t("patients.medicalCards.date")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.getMedicalCards.map((medicalCard) => (
                  <Tr key={medicalCard.id}>
                    <Td>
                      <Link
                        to={`/account/${medicalCard.patient.id}/visit/${medicalCard.id}`}
                        target={silent ? "_blank" : ""}
                      >
                        <AccountAvatar
                          account={medicalCard.doctor}
                          role={false}
                          size="sm"
                        />
                      </Link>
                    </Td>
                    <Td>{medicalCard.documents.length} </Td>
                    <Td>{medicalCard.comments.length} </Td>
                    <Td isNumeric>
                      {formatDateTime(
                        medicalCard.updatedAt || medicalCard.createdAt
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </>
      </Box>
    </>
  );
};
